import { TextField as CustomMuiTextField } from '@mui/material';
import { Autocomplete, Box } from '@mui/material';
import React, { SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsExclamationOctagon } from 'react-icons/bs';
import { BiRefresh } from 'react-icons/bi';
import { useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import { IAllStates } from '../../store/rootReducer';
import { formatGroupOptions, getCorrectColor } from '../functions/Functions';
import { CustomImageHandler } from './CustomImageHandler';
import Flag from './flag/Flag';

interface iAutocompleteProps {
    dataArray: any[];
    defaultArray?: any[] | string | number | null | any;
    setFilterState?: any;
    isWithImage: boolean;
    isLongSelected?: boolean;
    isMultiple?: boolean;
    inputLabel: string;
    changeEventKey?: string;
    photoLabel?: string;
    selectedStateImage?: string;
    queryFilterKey: string;
    inputLabelTranslation: string;
    classes?: string;
    handleChangeParams: (paramKey: string, paramValue: string | string[] | number) => void;
    isFreeSolo?: boolean;
    handleSpace?: any;
    hasError?: boolean;
    isChipAutocomplete?: boolean;
    disableClearable?: boolean;
    isOfferEnum?: boolean;
    isPhaseEnum?: boolean;
    isDisabled?: boolean;
    isRequired?: boolean;
    isManagingModal?: boolean;
    haveDots?: boolean;
    status?: string;
    limitTags?: number;
    isStatusLabelVisible?: boolean;
    disableClientFilter?: boolean;
    isLongCampaignSelected?: boolean;
    isAdditionalOnlySelected?: boolean;
    openDropdown?: boolean;
    handleOpenDropdown?: () => void;
    handleCloseDropdown?: () => void;
    forbiddenCharacters?: RegExp;
    me?: boolean;
    onScrollBottom?: () => void;
    truncateOptionLabel?: boolean;
    chipMaxWidth?: number;
    usePortal?: boolean;
    popperZIndex?: number;
}

const statusesWithIcon = ['proposed'];

export const CustomAutocomplete = ({
    isManagingModal,
    dataArray = [],
    defaultArray,
    setFilterState,
    isWithImage,
    inputLabel,
    photoLabel,
    selectedStateImage,
    handleChangeParams,
    changeEventKey,
    queryFilterKey,
    classes,
    inputLabelTranslation,
    handleSpace,
    isLongSelected = false,
    hasError = false,
    isMultiple = false,
    isDisabled = false,
    isOfferEnum = false,
    isChipAutocomplete = false,
    isFreeSolo = false,
    disableClearable = false,
    isRequired,
    haveDots,
    limitTags,
    isStatusLabelVisible = true,
    disableClientFilter = false,
    isLongCampaignSelected = false,
    isAdditionalOnlySelected = false,
    openDropdown,
    handleOpenDropdown,
    handleCloseDropdown,
    forbiddenCharacters,
    me,
    onScrollBottom,
    isPhaseEnum = false,
    truncateOptionLabel = false,
    chipMaxWidth,
    usePortal = false,
    popperZIndex,
}: iAutocompleteProps) => {
    const userType = useSelector((state: IAllStates) => state?.auth?.user?.userType);
    const { t } = useTranslation();

    const isSearchControlled = !!setFilterState;
    const [typedValue, setTypedValue] = useState('');

    const debounced = useDebouncedCallback((key, value) => {
        if (!setFilterState || !key) return;

        setFilterState((prev: any) => ({
            ...prev,
            [key]: value,
        }));
    }, 200);

    const getOptionId = (option: any) => {
        return (
            option?.userId ||
            option?.offers?.[0]?.id ||
            option?.id ||
            option?.key ||
            option?.username ||
            option?.name ||
            option?.displayName ||
            option?.displayName ||
            option
        );
    };

    const getBadgeKey = () => {
        if (isLongCampaignSelected) {
            return 'long_term';
        }

        if (isLongSelected) {
            return 'linked';
        }

        if (isAdditionalOnlySelected) {
            return 'addons_only';
        }

        return null;
    };

    const badgeKey = getBadgeKey();

    const displayInputValue =
        inputLabel === 'hashtag'
            ? typedValue?.includes(' ')
                ? typedValue.replace(' ', '_')
                : typedValue
            : isChipAutocomplete && typedValue?.includes(' ')
            ? ''
            : typedValue;

    return (
        <Autocomplete
            disablePortal={!usePortal}
            {...(popperZIndex !== undefined && {
                componentsProps: {
                    popper: {
                        sx: { zIndex: popperZIndex },
                    },
                },
            })}
            freeSolo={isFreeSolo}
            disabled={isDisabled}
            options={dataArray}
            value={defaultArray}
            limitTags={limitTags ?? 1}
            multiple={isMultiple}
            noOptionsText="Start typing"
            getOptionLabel={option => getCorrectKey(inputLabel, option, t)}
            size="small"
            id={`id_${inputLabel}`}
            isOptionEqualToValue={(option, value) => getOptionId(option) === getOptionId(value)}
            disableClearable={disableClearable}
            open={openDropdown}
            onOpen={handleOpenDropdown}
            onClose={handleCloseDropdown}
            openOnFocus
            filterOptions={disableClientFilter ? options => options : undefined}
            {...(isSearchControlled && {
                inputValue: displayInputValue,
                onInputChange: (event: SyntheticEvent, newInputValue: string, reason: string) => {
                    if (reason === 'reset') {
                        if (!isMultiple) {
                            setTypedValue(newInputValue);
                        }
                        return;
                    }

                    let value = newInputValue;

                    if (forbiddenCharacters) {
                        value = value.replace(forbiddenCharacters, '');
                    }

                    setTypedValue(value);

                    if (reason === 'input') {
                        debounced(changeEventKey, value);
                    } else if (reason === 'clear') {
                        debounced(changeEventKey, '');
                    }
                },
            })}
            ListboxProps={{
                style: { maxHeight: 300, overflowY: 'auto' },
                onScroll: (event: React.SyntheticEvent<HTMLUListElement>) => {
                    const node = event.currentTarget;
                    if (onScrollBottom && node.scrollTop + node.clientHeight >= node.scrollHeight - 50) {
                        onScrollBottom();
                    }
                },
            }}
            sx={{
                '.MuiOutlinedInput-root': {
                    padding: '0px 9px !important',
                },
                '.Mui-focused': {
                    padding: '9px !important',
                    paddingRight: '60px !important',
                },
                '.MuiAutocomplete-endAdornment': {
                    // top: 'auto',
                },
                '.MuiAutocomplete-tag': {
                    marginTop: '8px',
                },
                ...(chipMaxWidth && {
                    '.MuiChip-label': {
                        maxWidth: chipMaxWidth,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    },
                }),
            }}
            className={`w-100 ${classes ?? ''}`}
            renderOption={(props, option) => {
                return (
                    <Box
                        component="li"
                        sx={{
                            '& > img': { mr: 1, flexShrink: 0 },
                            ...(truncateOptionLabel && {
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'hidden',
                            }),
                        }}
                        {...props}
                        key={option?.key || option[changeEventKey as keyof typeof option]}
                    >
                        {photoLabel === 'countryFlag' && (
                            <div className="country-flag mr-2">
                                <Flag className="rounded" code={option} />
                            </div>
                        )}
                        {isWithImage && photoLabel !== 'countryFlag' && (
                            <CustomImageHandler
                                altTag="logo"
                                classes={`user-img-rounded ${!!isManagingModal ? 'ml-2' : ''} ${me && 'mr-2'}`}
                                photoPath={getCorrectKey(photoLabel as string, option, t) as string}
                                name={getCorrectKey(inputLabel, option, t) as string}
                            />
                        )}

                        {isOfferEnum && (
                            <div>
                                {statusesWithIcon.includes(option) ? (
                                    <span>
                                        {option === 'proposed' ? (
                                            <BsExclamationOctagon className="proposed-icon mr-1" />
                                        ) : (
                                            <BiRefresh className="proposed-icon text-dark mr-1" />
                                        )}
                                    </span>
                                ) : (
                                    <span className={`calendar-button-dot ${getCorrectColor(option)}`} />
                                )}
                            </div>
                        )}

                        {isPhaseEnum && (
                            <div>
                                <span className={`status-dot phase-${option} mr-1`} />
                            </div>
                        )}

                        {haveDots && (
                            <div
                                className={`offer-bullet dashboard-dot position-static align-middle mr-1 ${
                                    option?.isLongTerm
                                        ? 'bg-primary'
                                        : option?.offers[0]?.status === 'completed'
                                        ? 'bg-primary'
                                        : 'bg-danger'
                                }`}
                            ></div>
                        )}
                        <span
                            className={`${truncateOptionLabel ? 'text-truncate' : 'word-break-word'} align-middle`}
                            style={truncateOptionLabel ? { minWidth: 0, flex: '1 1 auto' } : undefined}
                        >
                            {getCorrectKey(inputLabel, option, t)}
                        </span>
                        {option?.groups?.length > 0 && inputLabel === 'clientNameAndOptions' && (
                            <>
                                <span className={`mx-1 ${truncateOptionLabel ? 'flex-shrink-0' : ''}`}> - </span>
                                <span className={`text-primary ${truncateOptionLabel ? 'flex-shrink-0' : ''}`}>
                                    {formatGroupOptions(option?.groups, false, 30)}
                                </span>
                            </>
                        )}

                        {option?.offers?.[0]?.isLongTerm && isStatusLabelVisible && (
                            <div
                                className={`custom-badge long-term ml-1 ${truncateOptionLabel ? 'flex-shrink-0' : ''} ${
                                    queryFilterKey === 'platformId' && 'd-none'
                                }`}
                            >
                                {t(
                                    `brand.campaign.briefing.overview_card.${
                                        !userType?.includes('brand') ? 'linked' : 'long_term'
                                    }`
                                )}
                            </div>
                        )}

                        {option?.isLongTerm && isStatusLabelVisible && (
                            <div
                                className={`custom-badge long-term ml-1 ${truncateOptionLabel ? 'flex-shrink-0' : ''} ${
                                    queryFilterKey === 'platformId' && 'd-none'
                                }`}
                            >
                                {t(
                                    `brand.campaign.briefing.overview_card.${
                                        !userType?.includes('brand') ? 'long_term' : 'long_term'
                                    }`
                                )}
                            </div>
                        )}
                        {(option?.offers?.[0]?.isAddonsOnly || option?.isAddonsOnly) && isStatusLabelVisible && (
                            <div
                                className={`custom-badge addons_only ml-1 ${
                                    truncateOptionLabel ? 'flex-shrink-0' : ''
                                } ${queryFilterKey === 'platformId' && 'd-none'}`}
                            >
                                {t(`brand.campaign.briefing.overview_card.addons_only`)}
                            </div>
                        )}
                    </Box>
                );
            }}
            renderInput={params => (
                <div style={{ position: 'relative' }}>
                    {isWithImage && selectedStateImage && params.inputProps.value && (
                        <CustomImageHandler
                            photoPath={selectedStateImage as string}
                            altTag="User Profile"
                            classes="selected-input-logo"
                        />
                    )}
                    {badgeKey && (
                        <div className={`custom-badge ${badgeKey} ml-1 selected-long-term`}>
                            {t(`brand.campaign.briefing.overview_card.${badgeKey}`)}
                        </div>
                    )}
                    <CustomMuiTextField
                        {...params}
                        label={inputLabelTranslation}
                        onKeyDown={handleSpace}
                        required={isRequired}
                        error={hasError}
                        variant="outlined"
                        inputProps={{
                            ...params.inputProps,
                            ...(!isSearchControlled && {
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    let value = e.target.value;

                                    if (forbiddenCharacters) {
                                        const sanitized = value.replace(forbiddenCharacters, '');

                                        if (sanitized !== value) {
                                            e.target.value = sanitized;
                                            value = sanitized;
                                        }
                                    }

                                    params.inputProps.onChange?.(e);
                                    debounced(changeEventKey, value);
                                },
                                value:
                                    inputLabel === 'hashtag'
                                        ? typeof params?.inputProps?.value === 'string' &&
                                          params?.inputProps?.value?.includes(' ')!
                                            ? params?.inputProps?.value?.replace(' ', '_')
                                            : params?.inputProps?.value
                                        : isChipAutocomplete && (params?.inputProps?.value as string)?.includes(' ')
                                        ? ''
                                        : params.inputProps.value,
                            }),
                            style: {
                                paddingLeft: `${
                                    isWithImage && selectedStateImage && params.inputProps?.value ? '30px' : '0px'
                                }`,
                                paddingRight: `${!disableClearable ? '4rem' : 0}`,
                            },
                        }}
                    />
                </div>
            )}
            onChange={(event: SyntheticEvent, selectedState: any, reason) => {
                const value = getCorrectQueryValue(selectedState, inputLabel, isMultiple ?? false, queryFilterKey);

                handleChangeParams(queryFilterKey, value);

                if (isMultiple && (reason === 'selectOption' || reason === 'removeOption')) {
                    setTypedValue('');
                    if (setFilterState) {
                        debounced(changeEventKey, '');
                    }
                }

                if (reason === 'clear' && setFilterState) {
                    debounced(changeEventKey, '');
                }
            }}
        />
    );
};

const getCorrectKey = (value: string, obj: any, t?: any) => {
    switch (value) {
        case 'user.displayName':
            return !!obj?.user?.displayName?.length
                ? obj?.user?.displayName
                : `${obj?.user?.firstName} ${obj?.user?.lastName}`;
        case 'user.profilePhoto':
            return obj?.user?.profilePhoto?.path;
        case 'name':
        case 'platform':
        case 'discovery.location':
        case 'influencer.interests':
            return obj?.name;
        case 'path':
            return obj?.path;
        case 'firstName':
        case 'clientNameAndOptions':
            return `${obj?.firstName} ${obj?.lastName}`;
        case 'displayName':
            return !!obj?.displayName?.length ? obj?.displayName : `${obj?.firstName} ${obj?.lastName}`;
        case 'profilePhoto.path':
            return obj?.profilePhoto?.thumbnail;
        case 'socialPlatform.path':
            return obj?.socialPlatform?.path;
        case 'groupOption':
            return obj?.label;
        case 'platformFilter':
            return obj
                ? String(obj)
                      .replace(/_/g, ' ')
                      .replace(/^./, (c: string) => c.toUpperCase())
                : '';
        case 'gender':
            return t(`general.gender.${obj}`);
        case 'moodash_gender':
            return t(`general.moodash_gender.${obj}`);
        case 'socialPlatform.name':
            return t(`general.platforms.${obj?.socialPlatform?.name}`);
        case 'Publication Period':
            return obj ? t(`brand.campaign.briefing.step8.${obj}`) : '';
        case 'Payment method':
            return obj ? t(`brand.campaign.briefing.step8.payment_method.${obj}`) : '';
        case 'Payment terms':
            return obj ? t(`brand.campaign.briefing.step8.payment_terms_type.${obj}`) : '';
        case 'Campaign Duration':
            return t(`brand.campaign.briefing.step3.campaign_duration_enum.${obj}`);
        case 'country':
            return t(obj === '' ? '' : `general.countries.${obj}`);
        case 'label':
        case 'options':
            return obj?.label;
        case 'industry':
            return !!obj ? t(`general.industry.${obj}`) : '';
        case 'nbEmployees':
            return !!obj ? t(`general.nbEmployees.${obj}`) : '';
        case 'position':
            return t(!obj ? '' : `general.positions.${obj}`);
        case 'title':
            return !!obj?.title ? obj?.title : '-';
        case 'nbCreators':
            return t(`general.nbCreators.${obj}`);
        case 'plannerStatus':
            return t(`brand.campaign.manage.${obj}`);
        case 'campaign_status':
            return t(`general.offerStatus.${obj}`);
        case 'campaign_filter_status':
            return t(`general.campaign_filter_status.${obj}`);
        case 'campaign_filter_phase':
            return obj ? t(`general.campaign_filter_phase.${obj}`) : '';
        case 'decline_reason':
            return t(`general.decline_reasons.${obj}`);
        case 'locale':
            return obj ? t(`general.global_preferences.${obj}`) : '';
        case 'growthRate.interval':
            return obj ? t(`brand.campaign.discovery.growthInterval.${obj}`) : '';
        case 'growthRate.valuePercentage':
            return obj ? obj?.name : '';
        case 'last_posted':
            return t(`brand.campaign.discovery.last_posted.${obj}`);
        case 'language.name':
        case 'audience.credibility':
        case 'audience.percentage':
            return obj?.name ?? '';
        case 'influencer.mentions':
        case 'influencer.usernames':
            return obj?.username ?? '';
        case 'collaboration_logo':
            return obj?.logoUrl ?? '';
        case 'collaboration_name':
            return obj?.name ?? '';
        case 'year':
            return typeof obj === 'number' ? String(obj) : obj?.year ?? '';
        default:
            return obj;
    }
};

const getCorrectQueryValue = (value: any, key: string, isMultiple: boolean, queryFilterKey?: string) => {
    switch (key) {
        case 'user.displayName':
            if (queryFilterKey === 'offerId') {
                return value?.offerId;
            } else if (queryFilterKey === 'userIds') {
                const allIds = value?.map((item: any) => item?.user?.id);
                const removeDuplicates = allIds?.filter((item: any, index: any) => allIds.indexOf(item) === index);
                return removeDuplicates;
            } else if (queryFilterKey === 'offerIds') {
                const allIds = value?.map((item: any) => item?.id);
                const removeDuplicates = allIds?.filter((item: any, index: any) => allIds.indexOf(item) === index);
                return removeDuplicates;
            }
            return value ? value?.user.id : '';
        case 'name':
        case 'displayName':
        case 'platformId':
        case 'title':
        case 'firstName':
        case 'clientNameAndOptions':
        case 'socialPlatform.name':
            if (queryFilterKey === 'offerId') {
                return value?.offerId;
            }
            if (queryFilterKey === 'campaignIdPlanner') {
                return value;
            }
            return isMultiple ? value?.map((value: { id: number }) => value?.id) : value ? value?.id : '';
        case 'audienceLocation':
            return value?.map((value: string) => value);
        case 'platform':
            if (queryFilterKey === 'platformFilter') {
                return value?.id;
            }
            return value?.name;
        case 'label':
            return value?.label;
        case 'options':
            return value;
        case 'offerId':
        case 'moodash_gender':
        case 'language.name':
        case 'influencer.interests':
        case 'influencer.mentions':
        case 'influencer.usernames':
        case 'influencer.topics':
        case 'influencer.hashtags':
        case 'moodash_audience_age':
            return value;
        case 'audience.credibility':
            return value?.value;
        case 'year':
            return typeof value === 'number' ? value : value?.id ?? '';
        case 'growthRate.valuePercentage':
            return value?.value;
        case 'groupOption':
            return isMultiple ? value?.map((v: { id: number }) => v?.id) : value ? value?.id : '';
        default:
            return value !== null ? value : '';
    }
};
